// Lazy one-time loader for the Google Maps JS API (weekly channel).
// The key is referrer-locked; see apps/app/.env.
//
// With loading=async, the script's onload fires before google.maps.Map
// exists, so the map pieces come from importLibrary (Google's way for this
// loader) and are handed back here rather than read off the global.

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: { maps?: unknown };
  }
}

export interface MapsLib {
  Map: any;
  LatLngBounds: any;
  AdvancedMarkerElement: any;
}

let ready: Promise<MapsLib> | null = null;
const importer = () => (window.google?.maps as { importLibrary?: (name: string) => Promise<any> } | undefined)?.importLibrary;

export function loadGoogleMaps(): Promise<MapsLib> {
  if (ready) return ready;
  const key = import.meta.env.VITE_GMAPS_BROWSER_KEY as string | undefined;
  if (!key) return Promise.reject(new Error("Maps key missing"));
  ready = new Promise<void>((resolve, reject) => {
    if (importer()) return resolve();
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly&loading=async&libraries=marker`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Maps failed to load"));
    document.head.appendChild(s);
  }).then(async () => {
    for (let i = 0; i < 50 && !importer(); i++) await new Promise((r) => setTimeout(r, 100));
    const load = importer();
    if (!load) throw new Error("Maps failed to load");
    const [maps, core, marker] = await Promise.all([load("maps"), load("core"), load("marker")]);
    return { Map: maps.Map, LatLngBounds: core.LatLngBounds, AdvancedMarkerElement: marker.AdvancedMarkerElement };
  });
  ready.catch(() => {
    ready = null; // a failed load can be tried again
  });
  return ready;
}
